/*
 * gs-model-picker.js — Goods Scene
 * Searchable Model picker backed by ALL goods_model metaobjects.
 *
 * WHY THIS EXISTS. Shopify renders at most 100 values per filter, and the
 * Model filter (goods.compatible_models) has several hundred. The native list
 * therefore cannot offer every model. Filtering itself has no such limit: a URL
 * carrying filter.p.m.goods.compatible_models=<Metaobject GID> is honoured for
 * ANY model. So only the list of choices is replaced here. Shopify still does
 * all of the filtering, from the URL, exactly as before.
 *
 * WHAT IT DOES.
 *  1. Takes over the PANEL of the native Model facet, on desktop and in the
 *     mobile drawer. The button, its label, its position in the bar and the
 *     drawer's own back / Clear / Apply controls stay Shopify's and Dawn's.
 *     The native checkbox list is hidden with CSS (.gs-mp-host), never removed.
 *     snippets/facets.liquid is NOT modified.
 *  2. Loads the model list from sections/gs-model-data.liquid through the
 *     Section Rendering API. Same origin, plain Liquid, no API token. It is
 *     loaded only when the picker is opened or a Model is already active.
 *  3. Picking a model rewrites the CURRENT URL — every other parameter kept,
 *     the Model parameter replaced, `page` dropped — and hands it to Dawn's own
 *     FacetFiltersForm.renderPage, the same call the filter chips use.
 *  4. Keeps the active Model alive when ANOTHER filter is changed. Dawn builds
 *     the next URL from the form's inputs, and a model outside the native 100
 *     has no checkbox there, so it would silently drop out. A hidden input
 *     carries it. See syncState().
 *  5. Writes the real model name into the fallback chip that
 *     gs-facet-search.js draws ("Model selected" -> "Model: X60 Max Master").
 *  6. On collection pages, narrows the list to the models that actually have
 *     a product in the CURRENT result set (Part Type, Category, Price and
 *     every other active filter included), read from
 *     sections/gs-model-scope.liquid. See "result-set scope" below. If that
 *     cannot be read, the list is exactly what it was without it.
 *
 * IF ANYTHING HERE FAILS, THE NATIVE FACET COMES BACK. The native list is only
 * hidden while .gs-mp-host is on the facet. When the model list cannot be
 * loaded the class and the picker are removed, and the shopper is left with
 * the ordinary Shopify list. Without JavaScript nothing here runs at all.
 *
 * LIFECYCLE. Dawn replaces the innerHTML of every .js-filter, of
 * #ProductGridContainer and of the .active-facets rows on each filter change;
 * those elements themselves persist. One observer on each calls sync(), which
 * is idempotent: a second pass finds everything in place and writes nothing.
 */
(function () {
  'use strict';

  var PARAM = 'filter.p.m.goods.compatible_models';
  var PARAM_BRAND = 'filter.p.m.goods.brand';
  var PARAM_DEVICE = 'filter.p.m.goods.device_type';
  var HOST = 'gs-mp-host';
  var DATA_SECTION = 'gs-model-data';
  var MAX_ROWS = 50; // rows drawn at once; the search narrows the rest
  var MAX_PAGES = 40;
  var CACHE_KEY = 'gs-mp:v3'; // v3: rows carry the device type and the stored brand reference
  var CACHE_TTL = 30 * 60 * 1000;
  var SCOPE_SECTION = 'gs-model-scope';
  var SCOPE_MAX_PAGES = 20; // 5,000 products; beyond that the scope is not used
  var SCOPE_TIMEOUT = 8000;
  var SCOPE_DROP = ['page', 'sort_by', 'section_id'];

  var cfg =
    document.currentScript ||
    document.querySelector('script[data-gs-model-picker]');
  var ds = (cfg && cfg.dataset) || {};
  var ROOT_URL =
    ds.gsRoot ||
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
    '/';
  // Storefront strings come from the locale files (gs.js.model_picker.*, via window.gsT); a data-gs-*-label
  // attribute on the script tag still overrides one. Templates keep their [placeholders] until used.
  function gsLabel(key) {
    return window.gsT ? window.gsT('model_picker.' + key) : '';
  }
  function fill(template, vars) {
    var s = String(template || '');
    Object.keys(vars).forEach(function (k) {
      s = s.split('[' + k + ']').join(vars[k]).split('{' + k + '}').join(vars[k]);
    });
    return s;
  }
  var T = {
    search: ds.gsSearchLabel || gsLabel('search'),
    loading: ds.gsLoadingLabel || gsLabel('loading'),
    updating: ds.gsUpdatingLabel || gsLabel('updating'),
    none: ds.gsNoneLabel || gsLabel('none'),
    selected: ds.gsSelectedLabel || gsLabel('selected'),
    selectedGeneric: ds.gsSelectedGenericLabel || gsLabel('selected_generic'),
    clear: ds.gsClearLabel || gsLabel('clear'),
    more: ds.gsMoreLabel || gsLabel('more'),
    aka: ds.gsAkaLabel || gsLabel('aka'),
    filter: ds.gsFilterLabel || gsLabel('filter'),
    chip: gsLabel('chip') || '[label]: [model]'
  };

  var data = null; // { models: [], byId: {}, brands: {} }
  var dataState = 'idle'; // idle | loading | ready | failed
  var query = ''; // shared by the desktop and the mobile picker

  /* ---------- small helpers ---------- */

  function esc(text) {
    return String(text == null ? '' : text)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  // A model is identified by the digits that end its GID. Liquid hands out the
  // bare number, the filter URL carries gid://shopify/Metaobject/<number>;
  // comparing on the number makes the two interchangeable.
  function idOf(value) {
    var m = /(\d+)\s*$/.exec(String(value == null ? '' : value));
    return m ? m[1] : '';
  }

  function gidOf(id) {
    return 'gid://shopify/Metaobject/' + id;
  }

  function norm(text) {
    return String(text == null ? '' : text).toLowerCase().trim();
  }

  // "Go Vac-300" and "govac300" should find each other.
  function compact(text) {
    return norm(text).replace(/[\s\-_.,\/()]+/g, '');
  }

  function uniq(list) {
    var out = [];
    for (var i = 0; i < list.length; i++) {
      if (list[i] && out.indexOf(list[i]) === -1) out.push(list[i]);
    }
    return out;
  }

  function currentParams() {
    return new URLSearchParams(window.location.search);
  }

  // The Model values in the page URL, exactly as written there.
  function activeRaw() {
    return uniq(currentParams().getAll(PARAM));
  }

  function activeIds() {
    return uniq(activeRaw().map(idOf));
  }

  /* ---------- page context (rendered by snippets/gs-model-picker.liquid) ---------- */

  // WHERE THE SCOPE COMES FROM, most trusted first.
  //  - What is SELECTED (Brand, Device Type) is read from the page URL. The URL
  //    is what Shopify filters by and it is current the moment Dawn changes
  //    it, so a selection can never be stale or missing here.
  //  - What the PAGE is (which brands have products, which device type the
  //    collection is for) comes from the context block the theme re-renders
  //    with every result set, and for brands from the Brand filter's own
  //    checkboxes when the block has nothing.
  function readContext() {
    var ctx = { index: 0, label: '', brandActive: [], brandAvailable: [], devices: [], source: 'none' };
    var raw = {};
    var el = document.querySelector('script[data-gs-model-picker-context]');

    if (el) {
      ctx.source = 'attributes';
      // Plain attributes first: they survive even if the JSON body does not.
      raw.index = el.getAttribute('data-gs-index');
      raw.total = el.getAttribute('data-gs-total');
      raw.deviceOwn = el.getAttribute('data-gs-device-own');
      try {
        var parsed = JSON.parse(el.textContent || '{}');
        for (var key in parsed) {
          if (Object.prototype.hasOwnProperty.call(parsed, key)) raw[key] = parsed[key];
        }
        ctx.source = 'json';
      } catch (error) {
        /* unreadable JSON: the attributes above and the page itself still apply */
      }
    }

    ctx.index = parseInt(raw.index, 10) || 0;
    ctx.label = String(raw.label || '');

    var params = currentParams();

    // Brand ------------------------------------------------------------------
    ctx.brandActive = uniq(
      params.getAll(PARAM_BRAND).join(',').split(',').map(idOf)
    );
    ctx.brandAvailable = uniq(String(raw.brandAvailable || '').split(',').map(idOf));
    if (!ctx.brandAvailable.length) {
      var inputs = document.querySelectorAll('#FacetFiltersForm input[type="checkbox"][name="' + PARAM_BRAND + '"]');
      var seen = [];
      for (var i = 0; i < inputs.length; i++) {
        if (!inputs[i].disabled) seen.push(idOf(inputs[i].value));
      }
      ctx.brandAvailable = uniq(seen);
    }

    // Device type --------------------------------------------------------------
    ctx.devices = deviceScope(raw, uniq(params.getAll(PARAM_DEVICE).map(norm)));
    return ctx;
  }

  // The device types that define the page, lower-cased. Empty = no device scope.
  //  1. an active Device Type filter (from the URL);
  //  2. the collection's own device type, stated explicitly by the theme from
  //     the collection's rule or its metafield;
  //  3. only then an inference: a device type EVERY product in the result set
  //     carries (its count equals the total). A count above zero is NOT enough:
  //     a handful of products carry two device types, so the Cordless collection
  //     still counts a few under Robotic Vacuum;
  //  4. otherwise nothing, so all-products and mixed brand pages keep every
  //     device type.
  function deviceScope(raw, active) {
    if (active.length) return active;

    var own = uniq([norm(raw.deviceOwn)]);
    if (own.length) return own;

    var total = parseInt(raw.total, 10) || 0;
    var counts = raw.deviceCounts || [];
    var covering = [];
    for (var i = 0; total > 0 && i < counts.length; i++) {
      if (counts[i] && (parseInt(counts[i][1], 10) || 0) >= total) covering.push(norm(counts[i][0]));
    }
    return uniq(covering);
  }

  /* ---------- finding the native Model facet ---------- */

  function isShell(el) {
    return !!(el && el.querySelector('summary'));
  }

  function findHosts(ctx) {
    var out = [];
    var all = document.querySelectorAll('.js-filter');
    var i;

    for (i = 0; i < all.length; i++) {
      if (isShell(all[i]) && all[i].querySelector('input[name="' + PARAM + '"]')) {
        out.push(all[i]);
      }
    }

    // A Model facet that currently has no values has no inputs to recognise it
    // by. The context carries its position in the filter list instead.
    if (ctx.index > 0) {
      var keys = [String(ctx.index), 'mobile-' + ctx.index];
      for (i = 0; i < keys.length; i++) {
        var el = document.querySelector('.js-filter[data-index="' + keys[i] + '"]');
        if (!isShell(el) || out.indexOf(el) !== -1) continue;
        var other = el.querySelector('input[type="checkbox"][name]');
        if (other && other.name !== PARAM) continue; // a different filter
        if (el.querySelector('price-range')) continue;
        out.push(el);
      }
    }
    return out;
  }

  function isMobileHost(host) {
    return host.classList.contains('mobile-facets__details');
  }

  function containerOf(host) {
    return isMobileHost(host)
      ? host.querySelector('.mobile-facets__submenu')
      : host.querySelector('.parent-display');
  }

  function rootOf(container) {
    for (var n = container.firstElementChild; n; n = n.nextElementSibling) {
      if (n.classList.contains('gs-mp')) return n;
    }
    return null;
  }

  /* ---------- data ---------- */

  function readCache() {
    try {
      var raw = window.sessionStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || !parsed.t || Date.now() - parsed.t > CACHE_TTL) return null;
      if (!parsed.rows || !parsed.rows.length) return null;
      return parsed;
    } catch (error) {
      return null;
    }
  }

  function writeCache(rows, brands) {
    try {
      window.sessionStorage.setItem(
        CACHE_KEY,
        JSON.stringify({ t: Date.now(), rows: rows, brands: brands })
      );
    } catch (error) {
      /* private mode, quota: the list is simply fetched again next time */
    }
  }

  function natural(a, b) {
    return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
  }

  // rows: [id, name, brandId, "aka|aka", deviceType, brandGid]   brands: { id: name }
  function setData(rows, brands) {
    var brandNames = {};
    var key;
    for (key in brands) {
      if (Object.prototype.hasOwnProperty.call(brands, key)) {
        brandNames[idOf(key)] = String(brands[key] == null ? '' : brands[key]);
      }
    }

    var models = [];
    var byId = {};
    for (var i = 0; i < rows.length; i++) {
      var row = rows[i];
      if (!row) continue;
      var id = idOf(row[0]);
      var name = String(row[1] == null ? '' : row[1]).trim();
      if (!id || !name || byId[id]) continue;
      var brand = idOf(row[2]) || idOf(row[5]); // either way of reading the reference
      var aka = String(row[3] == null ? '' : row[3])
        .split('|')
        .map(function (part) {
          return part.trim();
        })
        .filter(Boolean);
      var brandName = brandNames[brand] || '';
      var model = {
        id: id,
        name: name,
        brand: brand,
        brandName: brandName,
        device: norm(row[4]),
        aka: aka,
        nameN: norm(name),
        akaN: aka.map(norm),
        hay: norm(name + ' ' + aka.join(' ') + ' ' + brandName),
        hayC: [compact(name)].concat(aka.map(compact)).join('|')
      };
      byId[id] = model;
      models.push(model);
    }

    models.sort(function (a, b) {
      return natural(a.name, b.name) || natural(a.brandName, b.brandName);
    });

    var withBrand = 0;
    var withDevice = 0;
    for (var m = 0; m < models.length; m++) {
      if (models[m].brand) withBrand++;
      if (models[m].device) withDevice++;
    }
    data = { models: models, byId: byId, withBrand: withBrand, withDevice: withDevice };
    dataState = models.length ? 'ready' : 'failed';
  }

  function fetchPage(page, pageParam) {
    var url =
      ROOT_URL +
      (ROOT_URL.indexOf('?') === -1 ? '?' : '&') +
      'section_id=' + DATA_SECTION +
      '&' + encodeURIComponent(pageParam || 'page') + '=' + page;

    return fetch(url, { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var el = doc.querySelector('script[data-gs-model-data]');
        if (!el) throw new Error('model data missing from response');
        var json = JSON.parse(el.textContent);
        // A page that ignores the page number would hand back page 1 again and
        // leave the list silently incomplete. Refuse it instead.
        if (parseInt(json.page, 10) !== page) throw new Error('unexpected page ' + json.page);
        return json;
      });
  }

  function load() {
    if (dataState !== 'idle') return;
    dataState = 'loading';
    ensureScope(); // in parallel with the model list
    renderAll(); // show "Loading models..." while the list is on its way

    var cached = readCache();
    if (cached) {
      setData(cached.rows, cached.brands || {});
      if (dataState === 'ready') {
        sync();
        return;
      }
      dataState = 'loading';
    }

    fetchPage(1, 'page')
      .then(function (first) {
        var pages = Math.min(parseInt(first.pages, 10) || 1, MAX_PAGES);
        var rest = [];
        for (var p = 2; p <= pages; p++) rest.push(fetchPage(p, first.pageParam));
        return Promise.all(rest).then(function (others) {
          return [first].concat(others);
        });
      })
      .then(function (parts) {
        var rows = [];
        var brands = {};
        for (var i = 0; i < parts.length; i++) {
          rows = rows.concat(parts[i].models || []);
          var b = parts[i].brands || {};
          for (var key in b) {
            if (Object.prototype.hasOwnProperty.call(b, key)) brands[key] = b[key];
          }
        }
        setData(rows, brands);
        if (dataState !== 'ready') throw new Error('no models in response');
        var total = parseInt(parts[0].total, 10) || 0;
        if (total && data.models.length < total) {
          console.warn('[gs-model-picker] loaded ' + data.models.length + ' of ' + total + ' models');
        }
        writeCache(rows, brands);
        sync();
      })
      .catch(function (error) {
        console.warn('[gs-model-picker] model list unavailable, native filter restored', error);
        data = null;
        dataState = 'failed';
        sync();
      });
  }

  /* ---------- result-set scope (sections/gs-model-scope.liquid) ---------- */

  // WHAT THIS ADDS. Brand and Device Type say which models BELONG on a page.
  // They do not say which of them still have a product once Part Type, Category,
  // Price or Availability is applied, so a shopper could pick a model and land
  // on an empty page. The scope section lists the models found on the products
  // Shopify returns for the current URL; the picker offers only those.
  //
  // THE URL ASKED FOR is the page URL minus the Model filter (so the other
  // models stay on offer while one is selected), minus `page` and `sort_by`
  // (neither changes WHICH products match). Everything else is sent as it is.
  //
  // FAIL OPEN. Any failure - a page that does not load, a wrong page number, a
  // product count that does not add up, a timeout - discards the whole scope
  // for that URL and the picker shows what Brand and Device Type alone allow,
  // exactly as before. A partial scope is never used.
  //
  // STALE ANSWERS. Every answer is filed under the URL it was asked for. Only
  // the answer for the URL the shopper is on NOW is ever applied, so a slow
  // reply to an older filter state cannot overwrite a newer one.
  var scope = { key: '', state: 'off', ids: null, total: 0, pages: 0, cacheHit: false, error: '' };
  var scopeCache = {}; // normalised URL -> { ids, total, pages }
  var scopePending = {}; // normalised URL -> true while its request is running

  // Collections and product search. On search the scope is read from the PRODUCT
  // results of the same query and filters (type=product, see fetchScopePage);
  // the same fail-open rules apply, so an unverifiable answer restores the full list.
  function scopeSource() {
    var path = window.location.pathname;
    if (/\/collections\/[^\/]+\/?$/.test(path)) return 'collection';
    if (/\/search\/?$/.test(path)) return 'search';
    return 'none';
  }

  // Same filters in a different order are the same result set.
  function scopeQuery() {
    var pairs = [];
    currentParams().forEach(function (value, name) {
      if (name === PARAM || SCOPE_DROP.indexOf(name) !== -1 || value === '') return;
      pairs.push([name, value]);
    });
    pairs.sort(function (a, b) {
      return a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : a[1] < b[1] ? -1 : a[1] > b[1] ? 1 : 0;
    });
    var out = new URLSearchParams();
    for (var i = 0; i < pairs.length; i++) out.append(pairs[i][0], pairs[i][1]);
    return out.toString();
  }

  function scopeKey() {
    var qs = scopeQuery();
    return window.location.pathname + (qs ? '?' + qs : '');
  }

  function fetchScopePage(key, page) {
    var source = /\/search\/?(\?|$)/.test(key) ? 'search' : 'collection';
    var url = key + (key.indexOf('?') === -1 ? '?' : '&') + 'section_id=' + SCOPE_SECTION + '&page=' + page;
    // Search: ask for the PRODUCT results of the same query and filters only. The
    // section counts products against Shopify's total, so anything else in the set
    // fails the "walked N of total" check below and the full list is kept.
    if (source === 'search' && !/(^|[?&])type=/.test(key)) url += '&type=product';
    return fetch(url, { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var el = doc.querySelector('script[data-gs-model-scope]');
        if (!el) throw new Error('scope missing from response');
        var json = JSON.parse(el.textContent);
        if (json.source !== source) throw new Error('not a ' + source + ' response');
        if (parseInt(json.page, 10) !== page) throw new Error('unexpected page ' + json.page);
        return json;
      });
  }

  function loadScope(key) {
    var first;
    var work = fetchScopePage(key, 1)
      .then(function (json) {
        first = json;
        var pages = parseInt(json.pages, 10) || 1;
        if (pages > SCOPE_MAX_PAGES) throw new Error('too many pages: ' + pages);
        var rest = [];
        for (var p = 2; p <= pages; p++) rest.push(fetchScopePage(key, p));
        return Promise.all(rest);
      })
      .then(function (others) {
        var parts = [first].concat(others);
        var total = parseInt(first.total, 10) || 0;
        var seen = 0;
        var ids = {};
        var count = 0;
        for (var i = 0; i < parts.length; i++) {
          seen += parseInt(parts[i].count, 10) || 0;
          var list = parts[i].models || [];
          for (var m = 0; m < list.length; m++) {
            var id = idOf(list[m]);
            if (id && !ids[id]) {
              ids[id] = true;
              count++;
            }
          }
          // The same references as stored on the product: a JSON list of GIDs.
          var raw = parts[i].raw || [];
          for (var r = 0; r < raw.length; r++) {
            var found = String(raw[r] == null ? '' : raw[r]).match(/Metaobject\/\d+/g) || [];
            for (var f = 0; f < found.length; f++) {
              var rid = idOf(found[f]);
              if (rid && !ids[rid]) {
                ids[rid] = true;
                count++;
              }
            }
          }
        }
        // Every matching product must have been walked, or the list of models
        // may be short and a model with products would be hidden.
        if (seen !== total) throw new Error('walked ' + seen + ' of ' + total + ' products');
        return { ids: ids, count: count, total: total, pages: parts.length };
      });

    var timer;
    var timeout = new Promise(function (resolve, reject) {
      timer = window.setTimeout(function () {
        reject(new Error('timeout'));
      }, SCOPE_TIMEOUT);
    });
    return Promise.race([work, timeout]).then(
      function (result) {
        window.clearTimeout(timer);
        return result;
      },
      function (error) {
        window.clearTimeout(timer);
        throw error;
      }
    );
  }

  function applyScope(key, entry, cacheHit) {
    scope = {
      key: key,
      state: 'ready',
      ids: entry.ids,
      count: entry.count,
      total: entry.total,
      pages: entry.pages,
      cacheHit: cacheHit,
      error: ''
    };
  }

  // Called from sync(), so after every Dawn redraw. Does nothing until the
  // picker has been opened once (the model list is loading or loaded): a
  // shopper who never touches Model costs no request.
  function ensureScope() {
    var source = scopeSource();
    if (source !== 'collection' && source !== 'search') {
      if (scope.state !== 'off') scope = { key: '', state: 'off', ids: null, total: 0, pages: 0, cacheHit: false, error: '' };
      return;
    }
    if (dataState !== 'loading' && dataState !== 'ready') return;

    var key = scopeKey();
    if (scope.key === key && scope.state !== 'off') return; // this state is handled

    if (scopeCache[key]) {
      applyScope(key, scopeCache[key], true);
      return;
    }

    scope = { key: key, state: 'loading', ids: null, total: 0, pages: 0, cacheHit: false, error: '' };
    if (scopePending[key]) return; // already on its way; its answer will land here
    scopePending[key] = true;

    loadScope(key).then(
      function (entry) {
        delete scopePending[key];
        scopeCache[key] = entry; // correct for ITS url, whatever the page shows now
        if (scope.key !== key) return; // the shopper has moved on: do not apply
        applyScope(key, entry, false);
        renderAll();
      },
      function (error) {
        delete scopePending[key];
        if (scope.key !== key) return;
        scope = {
          key: key,
          state: 'failed',
          ids: null,
          total: 0,
          pages: 0,
          cacheHit: false,
          error: String((error && error.message) || error)
        };
        renderAll(); // fail open: the list as Brand and Device Type leave it
      }
    );
  }

  // The scope for the URL on screen, or null when there is none to apply.
  function liveScope() {
    if (scope.state !== 'ready' || scope.key !== scopeKey()) return null;
    return scope.ids;
  }

  function scopeBusy() {
    return scope.state === 'loading' && scope.key === scopeKey();
  }

  /* ---------- brand scope and search ---------- */

  // BRAND: an active Brand filter wins. Otherwise the brands that actually have
  // products in the current result set: on a brand collection that is the one
  // brand, on all-products or a broad search it is every brand.
  // DEVICE TYPE: see deviceScope(). Both apply together.
  //
  // A scope is applied only when the model list actually carries that piece of
  // information. If no model had a brand recorded, filtering by brand would
  // empty the list for a reason the shopper cannot see, so that one scope is
  // skipped and the other still applies. Within a scope that IS applied there
  // is no fallback to "everything": a model with no brand is not offered under
  // a brand, and an empty result stays empty. A model with no device type
  // recorded is the one exception - it is never hidden by the device scope.
  //
  // RESULT SET: applied last, on top of both. A model that is currently
  // selected is always kept, so it can be seen and cleared.
  function scopedModels(ctx) {
    var base = contextModels(ctx);
    var live = liveScope();
    if (!live) return base;
    var active = activeIds();
    return base.filter(function (model) {
      return live[model.id] === true || active.indexOf(model.id) !== -1;
    });
  }

  function contextModels(ctx) {
    var ids = ctx.brandActive.length ? ctx.brandActive : ctx.brandAvailable;
    var devices = ctx.devices;
    var byBrand = ids.length > 0 && data.withBrand > 0;
    var byDevice = devices.length > 0 && data.withDevice > 0;
    if (!byBrand && !byDevice) return data.models;
    return data.models.filter(function (model) {
      if (byBrand && ids.indexOf(model.brand) === -1) return false;
      if (byDevice && model.device && devices.indexOf(model.device) === -1) return false;
      return true;
    });
  }

  function rank(model, qn) {
    if (model.nameN === qn) return 0;
    if (model.nameN.indexOf(qn) === 0) return 1;
    for (var i = 0; i < model.akaN.length; i++) {
      if (model.akaN[i].indexOf(qn) === 0) return 2;
    }
    if ((' ' + model.nameN).indexOf(' ' + qn) !== -1) return 3;
    return 4;
  }

  function search(models, text) {
    var qn = norm(text);
    if (!qn) return models;
    var tokens = qn.split(/\s+/);
    var qc = compact(text);

    var hits = models.filter(function (model) {
      var all = true;
      for (var i = 0; i < tokens.length; i++) {
        if (model.hay.indexOf(tokens[i]) === -1) {
          all = false;
          break;
        }
      }
      if (all) return true;
      return !!qc && model.hayC.indexOf(qc) !== -1;
    });

    return hits
      .map(function (model) {
        return { model: model, rank: rank(model, qn) };
      })
      .sort(function (a, b) {
        return a.rank - b.rank || natural(a.model.name, b.model.name);
      })
      .map(function (entry) {
        return entry.model;
      });
  }

  /* ---------- navigation ---------- */

  function go(params) {
    var qs = params.toString();
    var Form = window.customElements && window.customElements.get('facet-filters-form');
    var grid = document.getElementById('ProductGridContainer');

    // Dawn's own renderer, the one its filter chips call. It needs these three
    // things in the page; when any is missing a plain navigation is the safe
    // way to reach the same URL.
    if (
      Form &&
      typeof Form.renderPage === 'function' &&
      grid &&
      grid.querySelector('.collection') &&
      document.getElementById('product-grid')
    ) {
      try {
        Form.renderPage(qs, null, true);
        window.setTimeout(sync, 0);
        return;
      } catch (error) {
        /* fall through to a full navigation */
      }
    }
    window.location.assign(window.location.pathname + (qs ? '?' + qs : ''));
  }

  function removeParams() {
    var params = currentParams();
    params.delete(PARAM);
    params.delete('page'); // the page number belongs to the old result set
    return params;
  }

  function removeHref() {
    var qs = removeParams().toString();
    return window.location.pathname + (qs ? '?' + qs : '');
  }

  // The exact value Shopify itself uses for this model when it is in the native
  // list, otherwise the GID built from the id.
  function valueFor(id) {
    var inputs = document.querySelectorAll('input[type="checkbox"][name="' + PARAM + '"]');
    for (var i = 0; i < inputs.length; i++) {
      if (idOf(inputs[i].value) === id) return inputs[i].value;
    }
    return gidOf(id);
  }

  function closeDesktop() {
    var hosts = document.querySelectorAll('.' + HOST + '.facets__disclosure[open]');
    for (var i = 0; i < hosts.length; i++) {
      hosts[i].removeAttribute('open');
      var summary = hosts[i].querySelector('summary');
      if (summary) {
        summary.setAttribute('aria-expanded', 'false');
        summary.focus();
      }
    }
  }

  function select(id) {
    if (!id) return;
    var active = activeIds();
    query = '';
    if (active.length === 1 && active[0] === id) {
      closeDesktop();
      return;
    }
    var params = removeParams();
    params.append(PARAM, valueFor(id));
    closeDesktop();
    go(params);
  }

  function clear() {
    query = '';
    go(removeParams());
  }

  /* ---------- rendering ---------- */

  function build() {
    var root = document.createElement('div');
    root.className = 'gs-mp';
    root.innerHTML =
      '<div class="gs-mp__current" hidden>' +
      '<span class="gs-mp__current-text"></span>' +
      '<button type="button" class="gs-mp__clear link underlined-link">' + esc(T.clear) + '</button>' +
      '</div>' +
      '<div class="gs-mp__search">' +
      '<input type="text" class="gs-mp__input" autocomplete="off" autocapitalize="off" autocorrect="off" spellcheck="false"' +
      ' placeholder="' + esc(T.search) + '" aria-label="' + esc(T.search) + '">' +
      '</div>' +
      '<p class="gs-mp__status" role="status" hidden></p>' +
      '<ul class="gs-mp__list list-unstyled" role="list"></ul>' +
      '<p class="gs-mp__more" hidden></p>';

    // Deliberately NO name attribute on the input: Dawn builds the filter URL
    // from every named input in the form.
    var input = root.querySelector('.gs-mp__input');

    input.addEventListener('input', function (event) {
      // Dawn listens for `input` on the whole facet form and would re-render
      // the collection on every keystroke.
      event.stopPropagation();
      query = input.value;
      renderAll();
    });

    input.addEventListener('keydown', function (event) {
      if (event.key === 'Enter') {
        event.preventDefault(); // never submit the facet form
        var first = root.querySelector('.gs-mp__option');
        if (first && norm(input.value)) select(first.getAttribute('data-gs-mp-id'));
      } else if (event.key === 'ArrowDown') {
        var option = root.querySelector('.gs-mp__option');
        if (option) {
          event.preventDefault();
          option.focus();
        }
      }
    });

    root.addEventListener('click', function (event) {
      var target = event.target;
      if (!target || !target.closest) return;
      var option = target.closest('.gs-mp__option');
      if (option && root.contains(option)) {
        event.preventDefault();
        select(option.getAttribute('data-gs-mp-id'));
        return;
      }
      if (target.closest('.gs-mp__clear')) {
        event.preventDefault();
        clear();
      }
    });

    root.addEventListener('keydown', function (event) {
      var option = event.target && event.target.closest && event.target.closest('.gs-mp__option');
      if (!option) return;
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      event.preventDefault();
      var item = option.parentNode;
      var next = event.key === 'ArrowDown' ? item.nextElementSibling : item.previousElementSibling;
      var button = next && next.querySelector('.gs-mp__option');
      if (button) button.focus();
      else if (event.key === 'ArrowUp') input.focus();
    });

    return root;
  }

  function setText(el, text) {
    if (el.textContent !== text) el.textContent = text;
  }

  function setHidden(el, hidden) {
    if (el.hidden !== hidden) el.hidden = hidden;
  }

  function render(root, ctx) {
    var active = activeIds();
    var input = root.querySelector('.gs-mp__input');
    var current = root.querySelector('.gs-mp__current');
    var status = root.querySelector('.gs-mp__status');
    var list = root.querySelector('.gs-mp__list');
    var more = root.querySelector('.gs-mp__more');

    if (input.value !== query && document.activeElement !== input) input.value = query;

    // Selected model
    if (active.length) {
      var names = [];
      if (data) {
        for (var a = 0; a < active.length; a++) {
          if (data.byId[active[a]]) names.push(data.byId[active[a]].name);
        }
      }
      setText(
        current.querySelector('.gs-mp__current-text'),
        names.length === active.length ? fill(T.selected, { models: names.join(', ') }) : T.selectedGeneric
      );
      setHidden(current, false);
    } else {
      setHidden(current, true);
    }

    if (dataState !== 'ready') {
      setText(status, T.loading);
      setHidden(status, dataState !== 'loading');
      if (list.firstChild) list.innerHTML = '';
      root.gsMpSig = '';
      setHidden(more, true);
      return;
    }

    // The models for this result set are on their way. Nothing is offered in
    // the meantime, so a model that is about to disappear cannot be picked.
    // A failure or the timeout ends this state and the list comes back.
    if (scopeBusy()) {
      setText(status, T.updating);
      setHidden(status, false);
      if (list.firstChild) list.innerHTML = '';
      root.gsMpSig = '';
      setHidden(more, true);
      return;
    }

    var scoped = scopedModels(ctx);
    var results = search(scoped, query);
    var shown = results.slice(0, MAX_ROWS);

    var brandsInScope = {};
    var brandCount = 0;
    for (var s = 0; s < scoped.length && brandCount < 2; s++) {
      if (!brandsInScope[scoped[s].brand]) {
        brandsInScope[scoped[s].brand] = true;
        brandCount++;
      }
    }
    var showBrand = brandCount > 1;
    var qn = norm(query);

    // Only rebuild the rows when what they show has changed: keeps focus and
    // scroll position, and keeps the observers quiet.
    var sig =
      qn + '\u0001' + active.join(',') + '\u0001' + (showBrand ? 1 : 0) + '\u0001' +
      shown.map(function (model) { return model.id; }).join(',');

    if (root.gsMpSig !== sig) {
      var html = '';
      for (var i = 0; i < shown.length; i++) {
        var model = shown[i];
        var on = active.indexOf(model.id) !== -1;
        var aka = '';
        if (qn && model.aka.length && model.nameN.indexOf(qn) === -1) {
          aka = '<span class="gs-mp__aka">' + esc(fill(T.aka, { names: model.aka.join(', ') })) + '</span>';
        }
        html +=
          '<li class="gs-mp__item">' +
          '<button type="button" class="gs-mp__option' + (on ? ' is-active' : '') + '"' +
          ' data-gs-mp-id="' + esc(model.id) + '" aria-pressed="' + (on ? 'true' : 'false') + '">' +
          '<span class="gs-mp__text"><span class="gs-mp__name">' + esc(model.name) + '</span>' + aka + '</span>' +
          (showBrand && model.brandName ? '<span class="gs-mp__brand">' + esc(model.brandName) + '</span>' : '') +
          '</button>' +
          '</li>';
      }
      list.innerHTML = html;
      root.gsMpSig = sig;
    }

    setText(status, T.none);
    setHidden(status, results.length > 0);

    if (results.length > shown.length) {
      setText(more, fill(T.more, { shown: shown.length, total: results.length }));
      setHidden(more, false);
    } else {
      setHidden(more, true);
    }
  }

  function teardown(host) {
    var container = containerOf(host);
    var root = container ? rootOf(container) : null;
    if (root) root.remove();
    if (host.classList.contains(HOST)) host.classList.remove(HOST);
  }

  function ensureHost(host, ctx) {
    var container = containerOf(host);
    if (!container) return;

    if (dataState === 'failed') {
      teardown(host); // the native list becomes visible again
      return;
    }

    var root = rootOf(container);
    if (!root) {
      root = build();
      if (isMobileHost(host)) {
        var back = container.querySelector('.mobile-facets__close-button');
        container.insertBefore(root, back ? back.nextSibling : container.firstChild);
      } else {
        container.insertBefore(root, container.firstChild);
      }
    }
    if (!host.classList.contains(HOST)) host.classList.add(HOST);

    if (!host.gsMpBound) {
      host.gsMpBound = true;
      // The <details> itself survives every Dawn re-render.
      host.addEventListener('toggle', function () {
        if (!host.hasAttribute('open')) return;
        load();
        // Desktop only: on a phone this would raise the keyboard over the list.
        if (isMobileHost(host)) return;
        var panel = containerOf(host);
        var field = panel && panel.querySelector('.gs-mp__input');
        if (field) field.focus();
      });
      host.addEventListener('pointerenter', load);
      host.addEventListener('focusin', load);
    }

    // Dawn's own "Clear" in the mobile submenu: make sure it drops the Model
    // parameter even for a model Shopify did not list.
    if (isMobileHost(host)) {
      var clearLink = host.querySelector('a.mobile-facets__clear');
      var href = removeHref();
      if (clearLink && clearLink.getAttribute('href') !== href) clearLink.setAttribute('href', href);
    }

    render(root, ctx);
  }

  /* ---------- keeping the active Model through other filter changes ---------- */

  // Dawn rebuilds the URL from the facet form's inputs whenever a checkbox,
  // the price range or the sort order changes. A Model inside Shopify's 100
  // has a checked checkbox in that form (hidden, but still submitted). A Model
  // outside the 100 has nothing, and would be dropped. One hidden input per
  // such value keeps it. It is removed again as soon as the URL no longer
  // carries the value, so it can never re-apply a Model the shopper removed.
  function hasNative(form, raw) {
    var id = idOf(raw);
    var inputs = form.querySelectorAll('input[type="checkbox"][name="' + PARAM + '"]');
    for (var i = 0; i < inputs.length; i++) {
      var input = inputs[i];
      if (!input.checked || input.disabled) continue;
      if (input.closest('.no-js-list')) continue; // Dawn deletes these before submitting
      if (input.value === raw || (id && idOf(input.value) === id)) return true;
    }
    return false;
  }

  function syncState() {
    var raw = activeRaw();
    var ids = ['FacetFiltersForm', 'FacetFiltersFormMobile'];

    for (var f = 0; f < ids.length; f++) {
      var form = document.getElementById(ids[f]);
      if (!form) continue;

      var want = [];
      for (var i = 0; i < raw.length; i++) {
        if (!hasNative(form, raw[i])) want.push(raw[i]);
      }

      var mine = form.querySelectorAll('input[data-gs-mp-state]');
      var same = mine.length === want.length;
      for (var m = 0; same && m < mine.length; m++) {
        if (mine[m].value !== want[m]) same = false;
      }
      if (same) continue;

      for (m = 0; m < mine.length; m++) mine[m].remove();
      for (i = 0; i < want.length; i++) {
        var hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = PARAM;
        hidden.value = want[i];
        hidden.setAttribute('data-gs-mp-state', '');
        form.appendChild(hidden);
      }
    }
  }

  /* ---------- real model name in the fallback chip ---------- */

  // gs-facet-search.js draws a chip labelled "Model selected" for a Model that
  // Shopify left out of its list. With the full list loaded the name is known.
  // Only the chip's text node is touched; if the name cannot be resolved the
  // generic label simply stays.
  function relabelChips(ctx) {
    if (!data) return;
    var chips = document.querySelectorAll('[data-gs-model-fallback]');
    for (var i = 0; i < chips.length; i++) {
      var model = data.byId[idOf(chips[i].getAttribute('data-gs-model-fallback'))];
      if (!model) continue;
      var inner = chips[i].querySelector('.active-facets__button-inner');
      var node = inner && inner.firstChild;
      if (!node || node.nodeType !== 3) continue;
      var text = fill(T.chip, { label: ctx.label || T.filter, model: model.name });
      if (node.nodeValue !== text) node.nodeValue = text;
    }
  }

  /* ---------- lifecycle ---------- */

  function renderAll() {
    var ctx = readContext();
    var roots = document.querySelectorAll('.' + HOST + ' .gs-mp');
    for (var i = 0; i < roots.length; i++) render(roots[i], ctx);
  }

  function observe(el) {
    if (!el || el.gsMpObserved) return;
    el.gsMpObserved = true;
    new MutationObserver(sync).observe(el, { childList: true });
  }

  function sync() {
    var ctx = readContext();
    var hosts = findHosts(ctx);
    var i;

    // A facet that stopped being the Model facet gives its panel back.
    var marked = document.querySelectorAll('.js-filter.' + HOST);
    for (i = 0; i < marked.length; i++) {
      if (hosts.indexOf(marked[i]) === -1) teardown(marked[i]);
    }

    if (hosts.length) ensureScope(); // before drawing, so the rows match the URL
    for (i = 0; i < hosts.length; i++) ensureHost(hosts[i], ctx);

    syncState();
    relabelChips(ctx);

    // A Model that is already active needs its name, so the list is fetched
    // straight away; otherwise it waits until the picker is opened.
    if (dataState === 'idle' && hosts.length && activeIds().length) load();
  }

  function bind() {
    var i;
    var filters = document.querySelectorAll('.js-filter');
    for (i = 0; i < filters.length; i++) observe(filters[i]);
    var rows = document.querySelectorAll('.active-facets');
    for (i = 0; i < rows.length; i++) observe(rows[i]);
    observe(document.getElementById('ProductGridContainer'));
    sync();
  }

  // What the picker currently sees, for checking a page from the console:
  //   gsModelPickerDebug()
  window.gsModelPickerDebug = function () {
    var ctx = readContext();
    var el = document.querySelector('script[data-gs-model-picker-context]');
    return JSON.stringify(
      {
        url: window.location.pathname + window.location.search,
        contextFound: !!el,
        contextSource: ctx.source,
        contextText: el ? String(el.textContent).slice(0, 600) : null,
        modelFilterIndex: ctx.index,
        brandActive: ctx.brandActive,
        brandAvailable: ctx.brandAvailable,
        devices: ctx.devices,
        dataState: dataState,
        models: data ? data.models.length : 0,
        modelsWithBrand: data ? data.withBrand : 0,
        modelsWithDevice: data ? data.withDevice : 0,
        offeredByContext: data ? contextModels(ctx).length : 0,
        offeredHere: data ? scopedModels(ctx).length : 0,
        scopeEnabled: scopeSource() === 'collection' || scopeSource() === 'search',
        scopeSource: scopeSource(),
        normalizedScopeUrl: scopeKey(),
        scopeAppliedNow: !!liveScope(),
        scopeRequestState: scope.state,
        scopeError: scope.error || null,
        scopeProductCount: scope.state === 'ready' ? scope.total : null,
        scopeModelCount: scope.state === 'ready' ? scope.count : null,
        scopePages: scope.state === 'ready' ? scope.pages : null,
        scopeCacheHit: scope.cacheHit,
        pickers: document.querySelectorAll('.' + HOST + ' .gs-mp').length
      },
      null,
      1
    );
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind);
  } else {
    bind();
  }
  document.addEventListener('shopify:section:load', bind);

  // The URL changes a moment before Dawn redraws the page. Re-check the hidden
  // state inputs right away so they never lag behind a removed Model.
  window.addEventListener('popstate', function () {
    window.setTimeout(sync, 0);
  });
  document.addEventListener(
    'click',
    function (event) {
      if (event.target && event.target.closest && event.target.closest('facet-remove')) {
        window.setTimeout(sync, 0);
      }
    },
    true
  );
})();